import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, MessageCircle, ChevronDown } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import WhatsAppCta from '../components/WhatsAppCta';
import { whatsappUrl } from '../utils/whatsapp';
import { trackWhatsAppClick } from '../utils/analytics';
import { submitContact } from '../api/contact.api';

const CONTACT_BLOCKS = [
  { icon: Mail,          label: 'Email',    value: 'hello@mydrai.co',     hint: 'Replies within one business day', href: 'mailto:hello@mydrai.co' },
  // WhatsApp block is rendered as a clickable link — `href` opens the WhatsApp
  // chat (see whatsappUrl helper, honours REACT_APP_WHATSAPP_NUMBER env var).
  { icon: MessageCircle, label: 'WhatsApp', value: 'Tap to chat',         hint: 'Mon–Fri, 9am – 6pm MYT',           href: whatsappUrl(), external: true, whatsapp: true },
  { icon: MapPin,        label: 'Office',   value: 'Kuala Lumpur, Malaysia', hint: 'Visits by appointment' },
  { icon: Clock,         label: 'Support',  value: 'Available after signing in', hint: 'Manage bookings and care coordination through your secure account.' },
];

const TOPICS = [
  'General enquiry',
  'Programme question',
  'Become a clinic partner',
  'Become a pharmacy partner',
  'Press / media',
  'Bug report',
  'Other',
];

export default function Contact() {
  // Client 2026-09-10: page-specific SEO overrides so Google shows a Contact-
  // Us-focused snippet instead of the site-wide default. Restores whatever
  // was there on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Contact Us | MyDr.Ai – AI Medical Weight Loss Platform Malaysia';

    const desc =
      'Get in touch with the MyDr.Ai team in Malaysia. Reach out for inquiries ' +
      'regarding our doctor-led, AI-assisted medical weight management programs.';
    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', desc);

    return () => {
      document.title = prevTitle;
      if (prevDesc != null) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', topic: TOPICS[0], message: ''
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  function set(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  // Client 2026-09-10: wired to POST /contact. Backend sends the enquiry
  // to hello@mydrai.co (reply-to set to the sender) plus a courtesy
  // acknowledgement back to the sender.
  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr('');
    try {
      await submitContact({
        name:    form.name,
        email:   form.email,
        phone:   form.phone,
        topic:   form.topic,
        message: form.message,
      });
      setSent(true);
    } catch (e2) {
      setErr(
        e2?.response?.data?.message
          || e2?.message
          || 'Sorry — we couldn\'t send your message. Please try again or email hello@mydrai.co directly.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHero
        eyebrow="Contact us"
        title="We'd love to hear from you."
        subtitle="Questions about the programme, partner clinic / pharmacy enquiries, press — drop us a line and a real human will reply."
      />

      <section className="bg-canvas">
        <div className="container-x py-20 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Info column */}
            <Reveal className="lg:col-span-5">
              <h2 className="text-2xl font-semibold tracking-tight">Reach us directly</h2>
              <p className="mt-3 text-ink-600 max-w-md">
                Prefer email or phone? Pick whichever works for you. Our team is small and
                responsive — your message lands with a real human.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {CONTACT_BLOCKS.map((b) => {
                  const Icon = b.icon;
                  const commonInner = (
                    <>
                      <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
                        <Icon size={18} />
                      </div>
                      <div className="mt-4 text-xs uppercase tracking-wide text-ink-500">{b.label}</div>
                      <div className="mt-1 text-ink-900 font-semibold">{b.value}</div>
                      <div className="mt-1 text-xs text-ink-500">{b.hint}</div>
                    </>
                  );
                  if (b.href) {
                    return (
                      <a
                        key={b.label}
                        href={b.href}
                        {...(b.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        onClick={b.whatsapp ? () => trackWhatsAppClick({ source_page: 'contact', cta_label: 'Tap to chat' }) : undefined}
                        className="card-soft p-5 block hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-brand-300"
                      >
                        {commonInner}
                      </a>
                    );
                  }
                  return (
                    <div key={b.label} className="card-soft p-5">
                      {commonInner}
                    </div>
                  );
                })}
              </div>

              <div className="card-soft mt-6 p-5">
                <div className="text-xs uppercase tracking-wide text-ink-500">For partners</div>
                <p className="mt-2 text-sm text-ink-700">
                  Operating a clinic or pharmacy in Malaysia and interested in
                  partnering? Email{' '}
                  <a className="font-medium text-brand-700" href="mailto:hello@mydrai.co">
                    hello@mydrai.co
                  </a>{' '}
                  with a one-line intro.
                </p>
              </div>

              <div className="mt-6">
                <WhatsAppCta sourcePage="contact" label="Talk to us on WhatsApp" />
              </div>
            </Reveal>

            {/* Form */}
            <Reveal className="lg:col-span-7" delay={120}>
              <div className="card-elevated p-6 sm:p-8">
                {sent ? (
                  <div className="py-10 text-center">
                    <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
                      <CheckCircle2 size={26} />
                    </div>
                    <h3 className="mt-5 text-2xl font-semibold tracking-tight">Message received</h3>
                    <p className="mt-3 text-ink-600 max-w-md mx-auto">
                      Thanks, {form.name || 'friend'} — we'll be in touch at{' '}
                      <strong className="text-ink-900">{form.email}</strong> shortly.
                    </p>
                    <button
                      className="btn-outline mt-8"
                      onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', topic: TOPICS[0], message: '' }); }}
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5">
                    <h3 className="text-xl font-semibold tracking-tight">Send a message</h3>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Full name">
                        <input
                          required
                          className="input-base"
                          value={form.name}
                          onChange={(e) => set('name', e.target.value)}
                          placeholder="Aisha binti Rahman"
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          required type="email"
                          className="input-base"
                          value={form.email}
                          onChange={(e) => set('email', e.target.value)}
                          placeholder="you@example.com"
                        />
                      </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Phone (optional)">
                        <input
                          className="input-base"
                          value={form.phone}
                          onChange={(e) => set('phone', e.target.value)}
                          placeholder="+60 12-345 6789"
                        />
                      </Field>
                      <Field label="Topic">
                        <div className="relative">
                          <select
                            className="input-base appearance-none pr-10"
                            value={form.topic}
                            onChange={(e) => set('topic', e.target.value)}
                          >
                            {TOPICS.map((t) => <option key={t}>{t}</option>)}
                          </select>
                          <ChevronDown
                            size={16}
                            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-500"
                            aria-hidden="true"
                          />
                        </div>
                      </Field>
                    </div>

                    <Field label="Message">
                      <textarea
                        required
                        rows={5}
                        className="input-base !h-auto py-3"
                        value={form.message}
                        onChange={(e) => set('message', e.target.value)}
                        placeholder="How can we help?"
                      />
                    </Field>

                    {err && (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 text-sm">
                        {err}
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-3 pt-2">
                      <p className="text-xs text-ink-500 max-w-xs">
                        By sending this, you agree to our privacy policy. We never share
                        your details with third parties.
                      </p>
                      <button
                        type="submit"
                        className="btn-brand"
                        disabled={submitting}
                      >
                        {submitting ? <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <Send size={16} />}
                        {submitting ? 'Sending…' : 'Send message'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <style>{`
        .input-base {
          width: 100%;
          height: 44px;
          background: white;
          border: 1px solid #D2D6DE;
          border-radius: 12px;
          padding: 0 14px;
          color: #023E73;
          font: inherit;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .input-base:focus {
          border-color: #0EB2AB;
          box-shadow: 0 0 0 3px rgba(14,178,171,0.18);
        }
        textarea.input-base { resize: vertical; }
      `}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-900 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
